import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, Plus, Check, BellPlus } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { api, web, errMsg, toList, avatarSrc } from "../../lib/api";
import { PageHeader, Loader, ErrorNote, Empty, positionsOf, Avatar } from "../../components/app/kit";
import { ARCHETYPES } from "../../components/app/Archetypes";

const hasType = (p, a) => p.primary_archetype === a || p.secondary_archetype === a;
const posOf = (p) => new Set([...(Array.isArray(p.positions) ? p.positions : []), p.position].filter(Boolean));
const POSITIONS = ["PG", "SG", "SF", "PF", "C"];
const sel = "rounded-full border border-[#1E293B] bg-[#162032] px-4 py-2 text-sm text-white";

const SaveAlertBar = ({ archetype, gy, pos, years, onChange }) => {
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await web.post("/recruiter/alerts", { archetype, grad_year: gy ? Number(gy) : null, position: pos || null });
      toast.success("Alert saved. New matches show under Alerts.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="save-alert-bar" className="no-print mb-6 flex flex-wrap items-center gap-2 rounded-2xl border border-[#39FF14]/30 bg-[#39FF14]/5 p-3">
      <select data-testid="alert-year-select" value={gy ?? ""} onChange={(e) => onChange({ gy: e.target.value })} className={sel}>
        <option value="">Any class</option>
        {years.map((y) => <option key={y} value={y}>Class of {y}</option>)}
      </select>
      <select data-testid="alert-position-select" value={pos ?? ""} onChange={(e) => onChange({ pos: e.target.value })} className={sel}>
        <option value="">Any position</option>
        {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <button data-testid="save-alert-button" onClick={save} disabled={busy}
        className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#39FF14] px-4 py-2 text-sm font-semibold text-[#0A0E1A] transition-colors hover:bg-[#30D911] disabled:opacity-60">
        <BellPlus className="h-4 w-4" />{busy ? "Saving…" : "Save alert"}
      </button>
    </div>
  );
};

const ArchetypeFilter = ({ rows, value, onChange }) => {
  const counts = useMemo(() => Object.fromEntries(ARCHETYPES.map(([a]) => [a, rows.filter((p) => hasType(p, a)).length])), [rows]);
  const chip = (key, label, n, title) => (
    <button key={key} title={title} data-testid={`archetype-filter-${key}`} onClick={() => onChange(key === "all" ? null : key)}
      className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 font-mono-gd text-xs uppercase transition-colors ${(value ?? "all") === key ? "bg-[#39FF14] text-[#0A0E1A]" : n ? "bg-[#162032] text-slate-200 hover:text-white" : "bg-[#162032]/50 text-slate-400 hover:text-slate-200"}`}>
      {label}{n != null && <span className={(value ?? "all") === key ? "text-[#0A0E1A]/70" : "text-slate-400"}>{n}</span>}
    </button>
  );
  return (
    <div className="no-print mb-6">
      <p className="mb-2 font-mono-gd text-[11px] uppercase tracking-[0.2em] text-slate-400">Playing style</p>
      <div data-testid="archetype-filter" className="flex gap-1.5 overflow-x-auto pb-2 lg:flex-wrap">
        {chip("all", "All styles", null)}
        {[...ARCHETYPES].sort(([a], [b]) => (b === value) - (a === value) || (counts[b] > 0) - (counts[a] > 0)).map(([a, d]) => chip(a, a, counts[a], d))}
      </div>
    </div>
  );
};

export default function Search() {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [saved, setSaved] = useState({});

  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, loading, error } = useApi(`/recruiter/search?q=${encodeURIComponent(term)}`);
  const rows = toList(data, ["players", "results"]);
  const [params, setParams] = useSearchParams();
  const archetype = params.get("archetype");
  const gy = params.get("gy");
  const pos = params.get("pos");
  const setArchetype = (a) => setParams(a ? { archetype: a, ...(gy ? { gy } : {}), ...(pos ? { pos } : {}) } : {}, { replace: true });
  const setExtra = (patch) => {
    const next = { archetype, gy, pos, ...patch };
    setParams(Object.fromEntries(Object.entries(next).filter(([, v]) => v)), { replace: true });
  };
  const thisYear = new Date().getFullYear();
  const years = [...new Set([...rows.map((p) => p.grad_year).filter(Boolean), ...Array.from({ length: 5 }, (_, i) => thisYear + i)])].sort();
  const shown = archetype ? rows.filter((p) => hasType(p, archetype) && (!gy || String(p.grad_year) === gy) && (!pos || posOf(p).has(pos))) : rows;

  const toggle = async (p) => {
    const on = saved[p.id] ?? p.saved;
    try {
      if (on) await api.delete(`/recruiter/players/${p.id}`);
      else await api.post(`/recruiter/players/${p.id}`);
      setSaved((s) => ({ ...s, [p.id]: !on }));
      toast.success(on ? "Removed from board" : `${p.name} added to board`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Every GAMEDAY team" title="Find prospects" />
      <label className="no-print mb-6 flex items-center gap-3 rounded-full border border-[#1E293B] bg-[#0F172A] px-5 py-3.5 focus-within:border-[#39FF14]">
        <SearchIcon className="h-5 w-5 text-[#39FF14]" />
        <input data-testid="recruiter-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, team or position" className="w-full bg-transparent text-white outline-none placeholder:text-slate-500" />
      </label>
      {!error && rows.length > 0 && <ArchetypeFilter rows={rows} value={archetype} onChange={setArchetype} />}
      {archetype && <SaveAlertBar archetype={archetype} gy={gy} pos={pos} years={years} onChange={setExtra} />}
      {loading ? <Loader label="Searching" /> : error ? <ErrorNote error={error} /> : !shown.length ? <Empty title={archetype ? `No ${archetype} prospects match` : "No prospects match"} text={archetype ? "Coaches set archetypes on player profiles. Try another style or clear the filter." : undefined} /> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((p) => {
            const on = saved[p.id] ?? p.saved;
            return (
              <div key={p.id} data-testid={`search-result-${p.id}`} className="flex min-w-0 items-center gap-4 rounded-2xl border border-[#1E293B] bg-[#0F172A] p-4 transition-colors hover:border-slate-600">
                <Avatar name={p.name ?? ""} src={avatarSrc(p.team_id, p)} />
                <div className="min-w-0 flex-1">
                  {on ? (
                    <Link to={`/recruiter/prospects/${p.id}`} className="block truncate font-display text-xl font-bold uppercase text-white hover:text-[#39FF14]">{p.name}</Link>
                  ) : <p className="truncate font-display text-xl font-bold uppercase text-white">{p.name}</p>}
                  <p className="truncate font-mono-gd text-xs uppercase tracking-wider text-slate-400">{positionsOf(p)} · {p.grad_year ?? "—"} · {p.team_name}</p>
                  {p.primary_archetype && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {[p.primary_archetype, p.secondary_archetype].filter(Boolean).map((a, i) => (
                        <button key={a} data-testid={`result-archetype-${p.id}-${i}`} onClick={() => setArchetype(a)}
                          className={`rounded-full px-2.5 py-1 font-mono-gd text-[10px] uppercase tracking-wider transition-colors ${i === 0 ? "bg-[#39FF14]/15 text-[#39FF14] hover:bg-[#39FF14]/25" : "bg-[#162032] text-slate-200 hover:text-white"}`}>{a}</button>
                      ))}
                    </div>
                  )}
                </div>
                <button data-testid={`search-toggle-${p.id}`} onClick={() => toggle(p)} className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold transition-colors ${on ? "bg-[#39FF14]/15 text-[#39FF14] ring-1 ring-[#39FF14]/40" : "bg-[#39FF14] text-[#0A0E1A]"}`}>
                  {on ? <><Check className="h-3.5 w-3.5" />On board</> : <><Plus className="h-3.5 w-3.5" />Add</>}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
